/**
 * PARAMETERS dialog (Faz 6.9, OCS named-parameters parity): the named
 * parameter table — name / expression / evaluated value / description.
 * Expression edits go through the ParameterTable engine (parse check +
 * cycle detection) and every accepted edit is one undoable constraint
 * step; the watcher re-solves the geometry automatically.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Flex,
    HStack,
    IconButton,
    Input,
    Text,
    VStack,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { useConstraintStore } from '../../core/constraints/constraintStore';
import { undoableConstraints } from '../../core/constraints/constraintOps';
import { evaluateParameter, ParamError } from '../../core/constraints/parameters';
import { useAppStore } from '../../state/store';
import { ManageDialogFrame, ManageDialogCloseFooter } from './ManageDialogFrame';

interface RowState {
    name: string;
    expression: string;
    description: string;
}

export function ParametersDialog() {
    const { t } = useTranslation();
    const onClose = useAppStore((s) => s.closeManageDialog);
    const parameters = useConstraintStore((s) => s.set.parameters);

    const [draft, setDraft] = useState<Record<string, RowState>>({});
    const [error, setError] = useState<string | null>(null);

    const rows = Object.entries(parameters);

    const rowOf = (name: string): RowState =>
        draft[name] ?? { name, expression: parameters[name]?.expression ?? '', description: parameters[name]?.description ?? '' };

    const commitRow = (original: string, next: RowState): void => {
        const trimmedName = next.name.trim();
        const trimmedExpr = next.expression.trim();
        setError(null);
        if (!trimmedName || !trimmedExpr) return;
        if (trimmedName !== original && parameters[trimmedName]) {
            setError(t('constraints.parameters.nameExists', { name: trimmedName }));
            return;
        }
        undoableConstraints(() => {
            const store = useConstraintStore.getState();
            if (trimmedName !== original) {
                store.renameParameters({ [original]: trimmedName });
            }
            try {
                store.setParameter(trimmedName, { expression: trimmedExpr, description: next.description.trim() || undefined });
            } catch (e) {
                // Cycle guard: leave the previous expression in place.
                if (parameters[original]) {
                    store.setParameter(original, { expression: parameters[original].expression, description: parameters[original].description });
                }
                setError((e as Error).message.replace(/^cycle:/, `${t('constraints.parameters.cycle')}: `));
            }
        }, t('constraints.parameters.editLabel'));
        setDraft((d) => {
            const nextDraft = { ...d };
            delete nextDraft[original];
            delete nextDraft[trimmedName];
            return nextDraft;
        });
    };

    const removeRow = (name: string): void => {
        setError(null);
        undoableConstraints(() => {
            useConstraintStore.getState().removeParameter(name);
        }, t('constraints.parameters.removeLabel'));
    };

    const addRow = (): void => {
        const prefix = 'p';
        let max = 0;
        for (const name of Object.keys(parameters)) {
            const m = new RegExp(`^${prefix}(\\d+)$`).exec(name);
            if (m) max = Math.max(max, Number(m[1]));
        }
        const name = `${prefix}${max + 1}`;
        undoableConstraints(() => {
            useConstraintStore.getState().setParameter(name, { expression: '0' });
        }, t('constraints.parameters.addLabel'));
    };

    const valueOf = (name: string): string => {
        try {
            return evaluateParameter(name, parameters).toFixed(4).replace(/\.?0+$/, '');
        } catch (e) {
            if (e instanceof ParamError && e.kind === 'cycle') return t('constraints.parameters.cycleValue');
            return t('constraints.parameters.invalidValue');
        }
    };

    return (
        <ManageDialogFrame
            title={t('constraints.parameters.title')}
            isOpen
            onClose={onClose}
            size="lg"
            footer={<ManageDialogCloseFooter onClose={onClose} />}
        >
            <VStack align="stretch" spacing={2} minH="0">
                <HStack px={1} spacing={2} fontSize="xs" color="text.secondary" textTransform="uppercase">
                    <Box w="120px" flexShrink={0}>{t('constraints.parameters.name')}</Box>
                    <Box flex="1" minW="0">{t('constraints.parameters.expression')}</Box>
                    <Box w="90px" flexShrink={0}>{t('constraints.parameters.value')}</Box>
                    <Box w="140px" flexShrink={0}>{t('constraints.parameters.description')}</Box>
                    <Box w="64px" flexShrink={0} />
                </HStack>
                <Box flex="1" minH="120px" overflowY="auto" css={{ '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: 'var(--color-border)' } }}>
                    <VStack align="stretch" spacing={1}>
                        {rows.map(([name]) => {
                            const row = rowOf(name);
                            return (
                                <HStack key={name} spacing={2} bg="bg.canvas" borderRadius="sm" px={1} py={1}>
                                    <Input
                                        size="sm"
                                        w="120px"
                                        flexShrink={0}
                                        fontFamily="mono"
                                        value={row.name}
                                        onChange={(e) => setDraft((d) => ({ ...d, [name]: { ...rowOf(name), name: e.target.value } }))}
                                        onBlur={() => commitRow(name, rowOf(name))}
                                    />
                                    <Input
                                        size="sm"
                                        flex="1"
                                        minW="0"
                                        fontFamily="mono"
                                        value={row.expression}
                                        onChange={(e) => setDraft((d) => ({ ...d, [name]: { ...rowOf(name), expression: e.target.value } }))}
                                        onBlur={() => commitRow(name, rowOf(name))}
                                    />
                                    <Box w="90px" flexShrink={0} fontFamily="mono" fontSize="sm" color="text.secondary" isTruncated>
                                        {valueOf(name)}
                                    </Box>
                                    <Input
                                        size="sm"
                                        w="140px"
                                        flexShrink={0}
                                        value={row.description}
                                        onChange={(e) => setDraft((d) => ({ ...d, [name]: { ...rowOf(name), description: e.target.value } }))}
                                        onBlur={() => commitRow(name, rowOf(name))}
                                    />
                                    <IconButton
                                        aria-label={t('constraints.parameters.remove')}
                                        icon={<FiTrash2 />}
                                        size="xs"
                                        variant="ghost"
                                        color="text.secondary"
                                        flexShrink={0}
                                        onClick={() => removeRow(name)}
                                    />
                                </HStack>
                            );
                        })}
                        {rows.length === 0 && (
                            <Text fontSize="sm" color="text.secondary" p={2}>
                                {t('constraints.parameters.empty')}
                            </Text>
                        )}
                    </VStack>
                </Box>
                {error && <Text fontSize="sm" color="red.400">{error}</Text>}
                <Flex justify="flex-start">
                    <HStack
                        as="button"
                        spacing={1}
                        px={2}
                        py={1}
                        borderRadius="sm"
                        bg="bg.active"
                        color="text.primary"
                        fontSize="sm"
                        onClick={addRow}
                        cursor="pointer"
                        flexShrink={0}
                    >
                        <FiPlus />
                        <Text>{t('constraints.parameters.add')}</Text>
                    </HStack>
                </Flex>
            </VStack>
        </ManageDialogFrame>
    );
}
